import React, { useState, useEffect, useRef } from "react";
import {
  Bell,
  X,
  Clock,
  CheckCircle,
  UserPlus,
  LogIn,
  LogOut,
  AlertCircle,
  Calendar,
} from "lucide-react";
import useAuthStore from "../store/useAuthStore";
import socketManager from "../api/socket";
import api from "../api/axios";
import { toast } from "react-toastify";

export default function NotificationBell() {
  const { user } = useAuthStore();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredBell, setHoveredBell] = useState(false);
  const [hoveredClear, setHoveredClear] = useState(false);
  const [hoveredNotification, setHoveredNotification] = useState(null);
  const [hoveredClose, setHoveredClose] = useState(false);
  const dropdownRef = useRef(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const isMobile = windowWidth <= 768;

  // Load notifications from API on mount
  useEffect(() => {
    if (user?.id) {
      // Small delay to ensure socket is connected and authenticated
      const timer = setTimeout(() => {
        fetchNotifications();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [user]);

  const fetchNotifications = async () => {
    try {
      const response = await api.get("/notifications?limit=50");
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unreadCount);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      toast.error("Error al cargar notificaciones");
    }
  };

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Listen to socket events
  useEffect(() => {
    if (!user) return;

    console.log(
      "NotificationBell: Setting up socket listeners for user:",
      user.id
    );

    // Ensure socket is connected before adding listeners
    const setupListeners = () => {
      const socket = socketManager.get();

      // First remove any existing listeners to prevent duplicates
      socket.off("newNotification");
      socket.off("notificationRead");
      socket.off("allNotificationsRead");
      socket.off("notificationDeleted");
      socket.off("allNotificationsDeleted");

      console.log(
        "NotificationBell: Socket connected:",
        socket.connected,
        "id:",
        socket.id
      );

      // Listen for new notifications
      socket.on("newNotification", (notification) => {
        console.log(
          "NotificationBell: Received new notification:",
          notification._id,
          notification.title
        );
        setNotifications((prev) => {
          // Check if notification already exists to avoid duplicates
          const exists = prev.some((n) => n._id === notification._id);
          if (exists) {
            console.log(
              "NotificationBell: Notification already exists, skipping:",
              notification._id
            );
            return prev;
          }
          console.log(
            "NotificationBell: Adding new notification:",
            notification._id
          );
          // Only increment unread count if notification is new and not read
          if (!notification.read) {
            setUnreadCount((current) => current + 1);
          }
          // Don't show toast here - NotificationToastListener will handle it
          return [notification, ...prev];
        });
      });

      // Listen for notification updates
      socket.on("notificationRead", ({ notificationId }) => {
        setNotifications((prev) =>
          prev.map((n) => (n._id === notificationId ? { ...n, read: true } : n))
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      });

      socket.on("allNotificationsRead", () => {
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
        setUnreadCount(0);
      });

      socket.on("notificationDeleted", ({ notificationId }) => {
        setNotifications((prev) =>
          prev.filter((n) => n._id !== notificationId)
        );
      });

      socket.on("allNotificationsDeleted", () => {
        setNotifications([]);
        setUnreadCount(0);
      });

      return socket;
    };

    // Setup listeners immediately
    const socket = setupListeners();

    return () => {
      console.log("NotificationBell: Cleaning up socket listeners");
      if (socket) {
        socket.off("newNotification");
        socket.off("notificationRead");
        socket.off("allNotificationsRead");
        socket.off("notificationDeleted");
        socket.off("allNotificationsDeleted");
      }
    };
  }, [user]);

  const markAsRead = async (notificationId) => {
    try {
      await api.patch(`/notifications/${notificationId}/read`);
      // Socket event will handle the UI update
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.patch("/notifications/read-all");
      // Socket event will handle the UI update
    } catch (error) {
      console.error("Error marking all as read:", error);
      toast.error("Error al marcar notificaciones");
    }
  };

  const clearAll = async () => {
    try {
      await api.delete("/notifications");
      // Socket event will handle the UI update
    } catch (error) {
      console.error("Error clearing notifications:", error);
      toast.error("Error al limpiar notificaciones");
    }
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen && unreadCount > 0) {
      // Mark all as read when opening
      setTimeout(() => markAllAsRead(), 2000);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Ahora mismo";
    if (minutes < 60)
      return `Hace ${minutes} minuto${minutes !== 1 ? "s" : ""}`;
    if (hours < 24) return `Hace ${hours} hora${hours !== 1 ? "s" : ""}`;
    if (days < 7) return `Hace ${days} día${days !== 1 ? "s" : ""}`;

    return date.toLocaleDateString("es-SV", {
      day: "numeric",
      month: "short",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  };

  const styles = {
    container: {
      position: "relative",
    },
    bellButton: {
      position: "relative",
      padding: "0.75rem",
      borderRadius: "var(--radius-lg)",
      border: "2px solid var(--gold)",
      backgroundColor: "transparent",
      color: "var(--gold)",
      cursor: "pointer",
      transition: "all 0.3s ease",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    bellButtonHover: {
      backgroundColor: "var(--gold)",
      color: "white",
      transform: "scale(1.05)",
    },
    badge: {
      position: "absolute",
      top: "-4px",
      right: "-4px",
      minWidth: "20px",
      height: "20px",
      borderRadius: "var(--radius-full)",
      backgroundColor: "var(--danger)",
      color: "white",
      fontSize: "var(--text-xs)",
      fontWeight: "700",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "0 4px",
      animation: "pulse 2s ease-in-out infinite",
    },
    dropdown: {
      position: "fixed",
      ...(isMobile
        ? {
            // Mobile: Bottom sheet style
            bottom: 0,
            left: 0,
            right: 0,
            top: "auto",
            width: "100%",
            maxHeight: "85vh",
            height: "auto",
            borderRadius: "var(--radius-xl) var(--radius-xl) 0 0",
            transform: isOpen ? "translateY(0)" : "translateY(100%)",
            transition: "transform 0.3s ease-out",
          }
        : {
            // Desktop: Dropdown style
            position: "absolute",
            top: "calc(100% + 0.5rem)",
            bottom: "auto",
            left: "auto",
            right: 0,
            width: "380px",
            maxHeight: "500px",
            borderRadius: "var(--radius-xl)",
            transform: "none",
            animation: "fadeIn 0.2s ease-out",
          }),
      backgroundColor: "var(--bg-elevated)",
      boxShadow: "var(--shadow-2xl)",
      border: "1px solid var(--border)",
      overflow: "hidden",
      zIndex: 9999,
      display: "flex",
      flexDirection: "column",
    },
    dropdownHeader: {
      padding: isMobile ? "var(--space-4)" : "1.5rem",
      borderBottom: "2px solid var(--gold)",
      background: "var(--gradient-premium)",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexShrink: 0,
      minHeight: "60px",
    },
    dropdownTitle: {
      fontFamily: "var(--font-display)",
      fontSize: "var(--text-lg)",
      fontWeight: "700",
      color: "white",
    },
    clearButton: {
      fontSize: "var(--text-sm)",
      color: "white",
      background: "rgba(255, 255, 255, 0.1)",
      border: "1px solid rgba(255, 255, 255, 0.2)",
      cursor: "pointer",
      padding: "0.25rem 0.75rem",
      borderRadius: "var(--radius-md)",
      transition: "all 0.2s ease",
      fontWeight: "500",
    },
    clearButtonHover: {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
      borderColor: "rgba(255, 255, 255, 0.3)",
      color: "white",
      transform: "translateY(-1px)",
    },
    notificationsList: {
      flex: 1,
      overflowY: "auto",
      overflowX: "hidden",
      padding: isMobile ? "var(--space-3)" : "0.5rem",
      maxHeight: isMobile ? "calc(85vh - 100px)" : "calc(500px - 100px)",
      WebkitOverflowScrolling: "touch",
    },
    notification: {
      padding: isMobile ? "var(--space-3)" : "1rem",
      marginBottom: "0.5rem",
      borderRadius: "var(--radius-lg)",
      backgroundColor: "var(--bg-secondary)",
      border: "1px solid var(--border-light)",
      cursor: "pointer",
      transition: "all 0.2s ease",
    },
    notificationUnread: {
      backgroundColor: "var(--gold-subtle)",
      borderColor: "var(--gold)",
    },
    notificationHover: {
      transform: "translateX(-4px)",
      boxShadow: "var(--shadow-md)",
    },
    notificationHeader: {
      display: "flex",
      alignItems: "flex-start",
      gap: "0.75rem",
    },
    notificationIcon: {
      width: "40px",
      height: "40px",
      borderRadius: "var(--radius-lg)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    notificationContent: {
      flex: 1,
    },
    notificationTitle: {
      fontFamily: "var(--font-body)",
      fontSize: "var(--text-sm)",
      fontWeight: "600",
      color: "var(--text)",
      marginBottom: "0.25rem",
    },
    notificationMessage: {
      fontSize: "var(--text-sm)",
      color: "var(--text-secondary)",
      lineHeight: 1.4,
    },
    notificationTime: {
      fontSize: "var(--text-xs)",
      color: "var(--text-muted)",
      marginTop: "0.5rem",
    },
    emptyState: {
      padding: isMobile ? "var(--space-8)" : "3rem",
      textAlign: "center",
      color: "var(--text-tertiary)",
    },
    emptyIcon: {
      width: "48px",
      height: "48px",
      margin: "0 auto 1rem",
      opacity: 0.5,
    },
    closeButton: {
      position: "absolute",
      top: "0.5rem",
      right: "0.5rem",
      padding: "0.25rem",
      background: "none",
      border: "none",
      color: "var(--text-muted)",
      cursor: "pointer",
      borderRadius: "var(--radius-md)",
      transition: "all 0.2s ease",
    },
    closeButtonHover: {
      backgroundColor: "var(--bg-tertiary)",
      color: "var(--text)",
    },
    mobileOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      zIndex: 9998,
    },
  };

  const notificationIcons = {
    visit_created: {
      icon: UserPlus,
      color: "var(--primary)",
      bg: "var(--primary-bg)",
    },
    visit_arrived: {
      icon: LogIn,
      color: "var(--success)",
      bg: "var(--success-bg)",
    },
    visit_exited: { icon: LogOut, color: "var(--info)", bg: "var(--info-bg)" },
    visit_cancelled: {
      icon: X,
      color: "var(--danger)",
      bg: "var(--danger-bg)",
    },
    multiple_pending: {
      icon: AlertCircle,
      color: "var(--warning)",
      bg: "var(--warning-bg)",
    },
    frequent_visitor: {
      icon: UserPlus,
      color: "var(--info)",
      bg: "var(--info-bg)",
    },
    new_user: {
      icon: UserPlus,
      color: "var(--success)",
      bg: "var(--success-bg)",
    },
  };

  return (
    <div style={styles.container} ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        onMouseEnter={() => setHoveredBell(true)}
        onMouseLeave={() => setHoveredBell(false)}
        style={{
          ...styles.bellButton,
          ...(hoveredBell ? styles.bellButtonHover : {}),
        }}
        aria-label="Notificaciones"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span style={styles.badge}>
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          {isMobile && (
            <div
              style={styles.mobileOverlay}
              onClick={() => setIsOpen(false)}
            />
          )}
          <div
            /* En telefono este panel sube desde abajo y toca el borde de la
               pantalla: la marca lo aparta de la barra de gestos (index.css). */
            data-hoja-inferior={isMobile ? 'true' : undefined}
            style={{
              ...styles.dropdown,
              // Force visibility on mobile
              ...(isMobile
                ? {
                    display: "flex",
                    visibility: "visible",
                    opacity: 1,
                  }
                : {}),
            }}
          >
            {isMobile && (
              <div
                style={{
                  width: "40px",
                  height: "4px",
                  backgroundColor: "var(--border-dark)",
                  borderRadius: "var(--radius-full)",
                  margin: "0.5rem auto",
                  opacity: 0.5,
                }}
              />
            )}
            <div style={styles.dropdownHeader}>
              <h3 style={styles.dropdownTitle}>Notificaciones</h3>
              {notifications.length > 0 && (
                <button
                  onClick={clearAll}
                  onMouseEnter={() => setHoveredClear(true)}
                  onMouseLeave={() => setHoveredClear(false)}
                  style={{
                    fontSize: "var(--text-sm)",
                    color: "white",
                    backgroundColor: hoveredClear
                      ? "rgba(255, 255, 255, 0.2)"
                      : "rgba(255, 255, 255, 0.1)",
                    border: hoveredClear
                      ? "1px solid rgba(255, 255, 255, 0.3)"
                      : "1px solid rgba(255, 255, 255, 0.2)",
                    cursor: "pointer",
                    padding: "0.25rem 0.75rem",
                    borderRadius: "var(--radius-md)",
                    transition: "all 0.2s ease",
                    fontWeight: "500",
                    transform: hoveredClear
                      ? "translateY(-1px)"
                      : "translateY(0)",
                  }}
                >
                  Limpiar todo
                </button>
              )}
            </div>

            <div style={styles.notificationsList}>
              {notifications.length === 0 ? (
                <div style={styles.emptyState}>
                  <Bell style={styles.emptyIcon} />
                  <p>No tienes notificaciones</p>
                </div>
              ) : (
                notifications.map((notification) => {
                  const iconConfig =
                    notificationIcons[notification.type] ||
                    notificationIcons.visit_created;
                  const Icon = iconConfig.icon;

                  return (
                    <div
                      key={notification._id}
                      onClick={() => markAsRead(notification._id)}
                      onMouseEnter={() =>
                        setHoveredNotification(notification._id)
                      }
                      onMouseLeave={() => setHoveredNotification(null)}
                      style={{
                        ...styles.notification,
                        ...(notification.read ? {} : styles.notificationUnread),
                        ...(hoveredNotification === notification._id
                          ? styles.notificationHover
                          : {}),
                      }}
                    >
                      <div style={styles.notificationHeader}>
                        <div
                          style={{
                            ...styles.notificationIcon,
                            backgroundColor: iconConfig.bg,
                            color: iconConfig.color,
                          }}
                        >
                          <Icon size={20} />
                        </div>
                        <div style={styles.notificationContent}>
                          <div style={styles.notificationTitle}>
                            {notification.title}
                          </div>
                          <div style={styles.notificationMessage}>
                            {notification.message}
                          </div>
                          <div style={styles.notificationTime}>
                            {formatTime(
                              notification.createdAt || notification.timestamp
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {!isMobile && (
              <button
                onClick={() => setIsOpen(false)}
                onMouseEnter={() => setHoveredClose(true)}
                onMouseLeave={() => setHoveredClose(false)}
                style={{
                  ...styles.closeButton,
                  ...(hoveredClose ? styles.closeButtonHover : {}),
                }}
              >
                <X size={18} />
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
